import type { BillingSubscription } from "@gmx/shared/signup";
import { Badge, Button, Callout, Card, Skeleton, useToast } from "@gmx/ui";
import { CreditCard, ExternalLink, MessageCircle } from "lucide-react";
import { openSupportChat } from "../../components/plans/PlanCta";
import { useUser } from "../../lib/auth";
import { billingStatus, noSubscriptionCopy, subscriptionPlanName, subscriptionPriceText, useBillingPortal, useBillingSummary } from "../../lib/billing";
import "./billing.css";

/** Settings → Billing: the subscription (GET /api/billing/summary) and Stripe's billing portal. */
export function BillingTab() {
  const summary = useBillingSummary();
  return (
    <div className="gfp-settings-grid">
      <Card title="Billing" subtitle="Your Grow My X subscription.">
        {summary.isPending ? (
          <div className="gf-stack" aria-busy="true">
            <Skeleton lines={3} height={14} />
            <Skeleton width={160} height={38} radius={6} />
          </div>
        ) : summary.isError ? (
          <Callout
            tone="danger"
            title="We couldn't load your billing"
            action={
              <Button size="sm" onClick={() => void summary.refetch()} loading={summary.isFetching}>
                Try again
              </Button>
            }
          >
            {summary.error.message}
          </Callout>
        ) : summary.data.subscription ? (
          <Subscription sub={summary.data.subscription} manageable={summary.data.manageable} />
        ) : (
          <NoSubscription />
        )}
      </Card>
    </div>
  );
}

function Subscription({ sub, manageable }: { sub: BillingSubscription; manageable: boolean }) {
  const status = billingStatus(sub);
  const price = subscriptionPriceText(sub);
  const pastDue = status.tone === "danger";
  return (
    <div className="gf-stack">
      {pastDue ? (
        <Callout tone="danger" title="Your last payment didn't go through">
          Update your card in billing to keep Grow My X running.
        </Callout>
      ) : null}
      <div className="gfp-kv billing-kv">
        <span className="gfp-kv__k">Plan</span>
        <span className="gfp-strong">{subscriptionPlanName(sub)}</span>
        <span className="gfp-kv__k">Status</span>
        <span className="billing-status">
          <Badge size="sm" tone={status.tone}>
            {status.label}
          </Badge>
          <span className="billing-status__text">{status.text}</span>
        </span>
        {price ? (
          <>
            <span className="gfp-kv__k">Price</span>
            <span className="gfp-kv__v">{price}</span>
          </>
        ) : null}
      </div>
      {status.hint && !pastDue ? <p className="billing-hint">{status.hint}</p> : null}
      {manageable ? <ManageBilling /> : <NotManageable />}
    </div>
  );
}

function ManageBilling() {
  const portal = useBillingPortal();
  const toast = useToast();
  return (
    <div className="billing-manage">
      <Button
        variant="primary"
        icon={<CreditCard size={16} />}
        iconRight={<ExternalLink size={14} />}
        loading={portal.isPending || portal.isSuccess}
        onClick={() => portal.mutate(undefined, { onError: (err) => toast.error(err) })}
      >
        Manage billing
      </Button>
      <p className="billing-manage__note">Change your plan, update your card or cancel, on Stripe's secure billing page.</p>
    </div>
  );
}

function NotManageable() {
  return (
    <Callout
      tone="info"
      action={
        <Button size="sm" icon={<MessageCircle size={14} />} onClick={openSupportChat}>
          Talk to us
        </Button>
      }
    >
      Changes to this subscription go through our team. Tell us what you need.
    </Callout>
  );
}

function NoSubscription() {
  const user = useUser();
  const copy = noSubscriptionCopy(user);
  const askUs = !user.parentId && user.plan !== "lifetime";
  return (
    <div className="gf-stack">
      <div>
        <p className="gfp-strong">{copy.title}</p>
        <p className="billing-hint">{copy.text}</p>
      </div>
      {askUs ? (
        <div>
          <Button icon={<MessageCircle size={16} />} onClick={openSupportChat}>
            Talk to us
          </Button>
        </div>
      ) : null}
    </div>
  );
}
